import { getAquilaApiClient } from "../api/aquilaApi/aquilaApi";
import {
    getBooleanFromString,
    getValueFromSearchParams,
    getWindowValue,
    WindowProperty,
} from "../utils/window.utils";

import { authStore } from "./auth";
import { zustandStore } from "./zustand.helpers";

export enum FeatureFlags {
    UAT_PACKAGED_INSIGHT = "uatPackagedInsights",
    UAT_PEER_BENCHMARKING = "uatPeerBenchmarking",
    UAT_STANDALONE = "uatStandalone",
    SUPPORT_MODE = "supportMode",
    SEARCH_FILTER_INVERSION = "searchFilterInversion",
    UNIFIED_SEARCH = "unifiedSearch",
    ALL_FEATURE_FLAGS_DROP_DOWN = "allFeatureFlagsDropDown",
    SUMMARIZE_INSIGHT = "summarizeInsight",
    FORGE_VIZ = "forgeViz",
    THUMBS_FEEDBACK_ON_CARD = "thumbsFeedbackCard",
    GLOBAL_WORKBENCH = "globalWorkbench",
    UNIFIED_SEARCH_SORT_DROPDOWN = "unifiedsearchsortdropdown",
    ENABLE_TEST_ACCOUNTS = "enableTestAccounts",
    CONTENT_CARD_V2 = "contentCardV2",
    QUICK_FILTERS = "quickFilters",
    UX_TREATMENTS = "uxTreatmentsOnSidePanel",
    AI_PACKAGED = "aiPackagedInsights",
    PLM_WRITE_BOTH_READ_ACC = "plmWriteBothReadAcc",
    PLM_WRITE_BOTH_READ_PLM = "plmWriteBothReadPlm",
    PLM_WRITE_AND_READ_PLM = "plmWriteAndReadPlm",
}

export type FeatureFlagsState = {
    [flag in FeatureFlags]: boolean;
};

export const initialState: FeatureFlagsState = {
    [FeatureFlags.SEARCH_FILTER_INVERSION]: false,
    [FeatureFlags.UNIFIED_SEARCH]: true,
    [FeatureFlags.SUMMARIZE_INSIGHT]: false,
    [FeatureFlags.FORGE_VIZ]: false,
    [FeatureFlags.THUMBS_FEEDBACK_ON_CARD]: false,
    [FeatureFlags.GLOBAL_WORKBENCH]: false,
    [FeatureFlags.UNIFIED_SEARCH_SORT_DROPDOWN]: false,
    [FeatureFlags.CONTENT_CARD_V2]: false,
    [FeatureFlags.QUICK_FILTERS]: false,
    [FeatureFlags.UX_TREATMENTS]: false,
    [FeatureFlags.AI_PACKAGED]: false,
    [FeatureFlags.PLM_WRITE_BOTH_READ_ACC]: false,
    [FeatureFlags.PLM_WRITE_BOTH_READ_PLM]: false,
    [FeatureFlags.PLM_WRITE_AND_READ_PLM]: false,

    // Always here
    [FeatureFlags.UAT_PACKAGED_INSIGHT]: false,
    [FeatureFlags.UAT_PEER_BENCHMARKING]: false,
    [FeatureFlags.UAT_STANDALONE]: false,
    [FeatureFlags.SUPPORT_MODE]: false,
    [FeatureFlags.ALL_FEATURE_FLAGS_DROP_DOWN]: false,
    [FeatureFlags.ENABLE_TEST_ACCOUNTS]: false,
};

export const featureFlagLabelMap: {
    [flag in FeatureFlags]: string;
} = {
    [FeatureFlags.UNIFIED_SEARCH]: "Unified Search",
    [FeatureFlags.SUMMARIZE_INSIGHT]: "Summarize Insight",
    [FeatureFlags.FORGE_VIZ]: "Forge Visualization",
    [FeatureFlags.THUMBS_FEEDBACK_ON_CARD]: "Thumbs Feedback on Card",
    [FeatureFlags.GLOBAL_WORKBENCH]: "Global Workbench",
    [FeatureFlags.QUICK_FILTERS]: "Quick Filters",
    [FeatureFlags.AI_PACKAGED]: "A&I Insights for Packaged Insights",
    [FeatureFlags.PLM_WRITE_BOTH_READ_ACC]: "PLM Write Both Read ACC",
    [FeatureFlags.PLM_WRITE_BOTH_READ_PLM]: "PLM Write Both Read PLM",
    [FeatureFlags.PLM_WRITE_AND_READ_PLM]: "PLM Write and Read PLM",

    // Always here
    [FeatureFlags.UAT_PACKAGED_INSIGHT]: "UAT Packaged Insight",
    [FeatureFlags.UAT_PEER_BENCHMARKING]: "UAT Peer Benchmarking",
    [FeatureFlags.UAT_STANDALONE]: "UAT Standalone Families",
    [FeatureFlags.SUPPORT_MODE]: "Support Mode",
    [FeatureFlags.SEARCH_FILTER_INVERSION]: "Search Filter Inversion",
    [FeatureFlags.ALL_FEATURE_FLAGS_DROP_DOWN]: "All Feature Flags Drop Down",
    [FeatureFlags.UNIFIED_SEARCH_SORT_DROPDOWN]: "Unified Search Sort Dropdown",
    [FeatureFlags.ENABLE_TEST_ACCOUNTS]: "ENABLE_TEST_ACCOUNTS",
    [FeatureFlags.CONTENT_CARD_V2]: "contentCardV2",
    [FeatureFlags.UX_TREATMENTS]: "UX Treatments on SidePanel",
};

export const READY_TO_UAT_FEATURES: FeatureFlags[] = [
    FeatureFlags.UAT_PACKAGED_INSIGHT,
    FeatureFlags.UAT_STANDALONE,
];

export interface FeatureFlagsActions {
    setFeatureFlags: (flags: Partial<FeatureFlagsState>) => void;
    resetFeatureFlags: () => void;
}

export const { store: FeatureFlagsStore } = zustandStore<
    FeatureFlagsState,
    FeatureFlagsActions
>("FeatureFlags", initialState, (set: any) => ({
    setFeatureFlags: (flags: Partial<FeatureFlagsState>) =>
        set(() => ({ ...flags })),
    resetFeatureFlags: () => set(() => ({ ...initialState })),
}));

const getAquilaFlags = async (
    requesterAlias: string,
): Promise<{ [key: string]: boolean }> => {
    try {
        const {
            settings: { aquilaConfig },
        } = authStore.getState();
        const aquilaFlags = await (
            await getAquilaApiClient()
        ).getAccessByAppConfig({
            requesterAlias,
            ...aquilaConfig.featureAccess,
        });

        return aquilaFlags.data
            .filter((f) => f.allowed)
            .reduce<{ [key: string]: boolean }>((obj, flag) => {
                return { ...obj, [flag.name!]: true };
            }, {});
    } catch (e: any) {
        return {};
    }
};

const isFeatureEnabledWithPrecedence = (
    flag: string,
    aquilaFlags: { [key: string]: boolean },
): boolean => {
    //top precedence set for value set from URL param
    const urlParamValue = getValueFromSearchParams(flag);
    if (urlParamValue !== undefined) {
        return getBooleanFromString(urlParamValue);
    }

    //second precedence set for value set from aquila
    if (flag in aquilaFlags) {
        return aquilaFlags[flag];
    }

    //third precedence set from weblab
    const weblabValue = getWindowValue(
        `${WindowProperty.IHUB_STATE}.weblabs.${flag}`,
    );
    if (weblabValue !== undefined) {
        return !!weblabValue;
    }

    // Fourth precedence set from initial state
    return initialState[flag] ?? false;
};

export const getFlagValues = async (
    requesterAlias: string,
): Promise<FeatureFlagsState> => {
    const aquilaFlags = await getAquilaFlags(requesterAlias);
    return Object.keys(initialState).reduce((obj, flag) => {
        if (flag in obj) {
            return {
                ...obj,
                [flag]: isFeatureEnabledWithPrecedence(flag, aquilaFlags),
            };
        }
        return {
            ...obj,
        };
    }, initialState);
};
